import type { Post } from './types';

export function sortPostsByDate(posts: Post[]): Post[] {
  return [...posts].sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });
}

export function filterPostsByType(posts: Post[], type?: string): Post[] {
  if (!type || type === 'all') return posts;
  return posts.filter(post => post.type === type);
}

export function filterPinnedPosts(posts: Post[]): Post[] {
  return posts.filter(post => post.isPinned);
}

export function filterPostsByTier(posts: Post[], tier?: string): Post[] {
  if (!tier || tier === 'all') return posts;

  return posts.filter(post => {
    const postAccess = post.publishingOptions?.access;

    if (tier === 'public') {
      return postAccess === 'public';
    } else if (tier === 'subscribers') {
      return postAccess === 'paid_members' || postAccess === 'members';
    }

    return true;
  });
}

export function filterPostsByDateRange(posts: Post[], dateRange?: { startDate: string; endDate: string }): Post[] {
  if (!dateRange || (!dateRange.startDate && !dateRange.endDate)) return posts;

  return posts.filter(post => {
    const postDate = post.createdAt?.toDate?.() || new Date(post.createdAt);
    const postDateStr = postDate.toISOString().split('T')[0]; // Get YYYY-MM-DD format

    if (dateRange.startDate && dateRange.endDate) {
      return postDateStr >= dateRange.startDate && postDateStr <= dateRange.endDate;
    } else if (dateRange.startDate) {
      return postDateStr >= dateRange.startDate;
    } else if (dateRange.endDate) {
      return postDateStr <= dateRange.endDate;
    }

    return true;
  });
}

export function filterPostsBySearch(posts: Post[], searchTerm?: string): Post[] {
  if (!searchTerm || searchTerm.trim() === '') return posts;

  const searchLower = searchTerm.toLowerCase().trim();

  return posts.filter(post => {
    const titleMatch = post.title?.toLowerCase().includes(searchLower);
    const contentMatch = post.content?.toLowerCase().includes(searchLower);

    return titleMatch || contentMatch;
  });
}

export function applyAllFilters(
  posts: Post[],
  filters: {
    tier?: string;
    dateRange?: { startDate: string; endDate: string };
    search?: string;
  }
): Post[] {
  let filteredPosts = [...posts];

  // Apply tier filter
  if (filters.tier) {
    filteredPosts = filterPostsByTier(filteredPosts, filters.tier);
  }

  // Apply date range filter
  if (filters.dateRange) {
    filteredPosts = filterPostsByDateRange(filteredPosts, filters.dateRange);
  }

  // Apply search filter
  if (filters.search) {
    filteredPosts = filterPostsBySearch(filteredPosts, filters.search);
  }

  return filteredPosts;
}